import type React from 'react';
import { startTransition, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrbitControls } from '@react-three/drei';
import { Canvas, useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import { Button } from '@/components/ui/button';
import useClickOutside from '@/hooks/use-click-outside';

import type { Node } from '../state';
import type { useModal } from '../../../../../components/modals/new/use-modal';
import { Modal } from '../../../../../components/modals/new/modal';

export interface SphericalViewerModalProps {
  image: Node;
  show360Toggle?: boolean;
  modal: ReturnType<typeof useModal>;
}

export const SphericalScene: React.FC<{ image: Node }> = ({ image }) => {
  const texture = useLoader(THREE.TextureLoader, image.panorama);

  // Flip the texture horizontally to make it look like a mirror
  texture.repeat.x = -1;
  texture.offset.x = 1;
  texture.needsUpdate = true;

  const orbitControlsRef = useRef<OrbitControlsImpl>(null);

  useEffect(() => {
    startTransition(() => {
      if (orbitControlsRef.current) {
        orbitControlsRef.current?.target.set(0, 0, 0);
        orbitControlsRef.current?.update();
      }
    });
  }, []);

  return (
    <>
      <OrbitControls
        ref={orbitControlsRef}
        enableZoom={false}
        rotateSpeed={-1}
        zoomSpeed={4}
        minDistance={0.1}
        maxDistance={400}
        maxPolarAngle={2}
        minPolarAngle={1.15}
        makeDefault
      />

      {/* Spherical Mesh for Panorama */}
      <mesh>
        <sphereGeometry args={[500, 60, 40]} />
        <meshBasicMaterial map={texture} side={THREE.BackSide} />
      </mesh>
    </>
  );
};

export const SphericalViewerModal: React.FC<SphericalViewerModalProps> = ({
  image,
  show360Toggle = false,
  modal,
}) => {
  const [show360View, setShow360View] = useState(true);
  const modalContainerRef = useClickOutside<HTMLDivElement>(modal.dismiss, true);
  const { t } = useTranslation();

  return (
    <Modal
      ref={modalContainerRef}
      id="canvasBase"
      className="mx-8 my-8 w-full max-w-5xl p-0 2xl:max-w-screen-2xl"
      overlayWrapperClassname="z-[99999999]"
      {...modal}
      title={image.name}
    >
      <Modal.Body>
        <div className="flex justify-between rounded-t bg-[#061B25] px-5 py-4 text-white">
          <div className="flex items-center gap-2">
            <span className="text-base">{image.name}</span>
            {show360Toggle && (
              <Button
                className="border-white text-white"
                onClick={() => setShow360View(!show360View)}
                variant="outline"
                size="sm"
              >
                {show360View ? t('panoramaView') : t('360View')}
              </Button>
            )}
          </div>
        </div>
        <div className="mx-auto aspect-video max-w-2xl xl:max-w-screen-2xl">
          {show360View ? (
            <Suspense
              fallback={<div className="aspect-video text-center">Loading Panorama...</div>}
            >
              <Canvas camera={{ position: [0, 0, 3], fov: 50 }} frameloop="demand">
                <SphericalScene image={image} />
              </Canvas>
            </Suspense>
          ) : (
            <img src={image.panorama} alt="Panorama" className="aspect-video w-full" />
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
